import {
  DfpFinancialReadingResponse,
  DfpFinancialReadingTarget,
} from '@interfaces/dfp/response/dfpFinancialReadingResponse';
import { DfpModuleKey } from '@features/dashboard/performance/pages/dfp/services/dfp-modules.state';

/**
 * O que a faixa da leitura do Cog mostra (card 3801, protótipo `leitura-estados`). Separado do componente para ser
 * testado sem TestBed.
 */
export interface FinancialReadingView {
  /** Aviso no topo da faixa (o estado em si). `ready` sem pedido novo não tem aviso. */
  notice: 'none' | 'waiting' | 'generating' | 'failed' | 'insufficient' | null;
  /** Leitura pronta para mostrar embaixo do aviso (a última que existe). */
  showCurrent: boolean;
  /** Leitura anterior apagada enquanto a nova não chega (selo "Atualizando"). */
  updating: boolean;
  /** Pede uma releitura de segurança (sem tempo real o estado não muda sozinho na tela). */
  shouldPoll: boolean;
}

export function describeFinancialReading(
  reading: DfpFinancialReadingResponse | null
): FinancialReadingView {
  if (!reading) return { notice: null, showCurrent: false, updating: false, shouldPoll: false };

  const hasCurrent = !!reading.current?.findings?.length || !!reading.current?.summary;
  const inProgress = reading.state === 'waiting' || reading.state === 'generating';
  const updating = hasCurrent && (inProgress || (reading.stale && reading.state !== 'failed'));

  if (updating) {
    // Leitura antiga visível e apagada; o aviso é o selo "Atualizando", não o bloco de "escrevendo".
    return { notice: null, showCurrent: true, updating: true, shouldPoll: true };
  }

  switch (reading.state) {
    case 'ready':
      return { notice: null, showCurrent: hasCurrent, updating: false, shouldPoll: false };
    case 'failed':
      // A falha não apaga a última leitura boa: o aviso vem em cima e ela continua embaixo.
      return { notice: 'failed', showCurrent: hasCurrent, updating: false, shouldPoll: false };
    case 'insufficient':
      return { notice: 'insufficient', showCurrent: false, updating: false, shouldPoll: false };
    case 'waiting':
      return { notice: 'waiting', showCurrent: false, updating: false, shouldPoll: true };
    case 'generating':
      return { notice: 'generating', showCurrent: false, updating: false, shouldPoll: true };
    default:
      return { notice: 'none', showCurrent: false, updating: false, shouldPoll: false };
  }
}

/** Módulo da barra "Mostrar" que o achado aponta (`dre` do contrato é o módulo `income`). */
export function findingModule(target: DfpFinancialReadingTarget): DfpModuleKey {
  return target === 'dre' ? 'income' : target;
}

/** Texto do atalho do achado para o bloco que o explica. */
export const FINDING_TARGET_LINK: Record<DfpFinancialReadingTarget, string> = {
  dre: 'Ver na DRE',
  balance: 'Ver no Balanço',
  highlights: 'Ver nos pontos de destaque',
  revenue: 'Ver no Faturamento',
};
